// src/contexts/FeatureFlagsContext.js
// Fetches backend feature flags (GET /api/v2/config/features) once per session/org, then every 5 minutes
// while signed in with an organization selected, and exposes them app-wide. Used to route risk/remediation
// mutations to the unified endpoints when `useUnifiedRisks` is enabled. Mirrors the LicenseContext pattern.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";

const FeatureFlagsContext = createContext();

// Offline licence status used when /config/features has not loaded or an older backend omits the
// `licence` field: behaves like the SaaS (no licence needed, no banner).
export const DEFAULT_LICENCE_STATUS = Object.freeze({ required: false, state: "notRequired" });

// While signed in with an organization selected, the flags (and the licence block) are re-fetched
// this often, so a licence expiry transition or another SuperAdmin's upload shows without a reload.
// Matches the backend's licence state refresh interval.
export const FLAGS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// True when localStorage holds a selected organization with an id.
const hasSelectedOrganization = () => {
  const selectedOrg = localStorage.getItem("selectedOrganization");
  if (!selectedOrg) return false;
  try {
    return Boolean(JSON.parse(selectedOrg)?.organizationId);
  } catch {
    return false;
  }
};

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);
  // True once the first /config/features round trip has settled (success or failure), so a
  // route guard on an opt-in flag can wait instead of redirecting on the initial empty flags.
  const [loaded, setLoaded] = useState(false);
  const { isAuthenticated, authVersion } = useAuth();

  const fetchFlags = useCallback(async () => {
    if (!hasSelectedOrganization()) {
      setFlags({});
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/config/features");
      setFlags(response.data || {});
    } catch {
      setFlags({});
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  // The periodic re-fetch: silent (no loading flip), and a failed request keeps the flags already
  // shown, so a transient network error does not briefly switch features or the banner off.
  const pollFlags = useCallback(async () => {
    if (!hasSelectedOrganization()) {
      setFlags({});
      return;
    }
    try {
      const response = await api.get("/config/features");
      setFlags(response.data || {});
    } catch {
      // Keep the previous flags; the next poll retries.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !hasSelectedOrganization()) return undefined;
    fetchFlags();
    const interval = setInterval(pollFlags, FLAGS_REFRESH_INTERVAL_MS);
    // Cleared on unmount, on sign-out (isAuthenticated turns false) and when the session changes.
    return () => clearInterval(interval);
  }, [isAuthenticated, authVersion, fetchFlags, pollFlags]);

  const licence =
    flags.licence && typeof flags.licence === "object" ? flags.licence : DEFAULT_LICENCE_STATUS;

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, loaded, licence, refetchFlags: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

// Returns the full flags object.
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context.flags;
};

// Convenience hook: true when the unified-risks read/write path is enabled for this org.
export const useUnifiedRisks = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useUnifiedRisks must be used within a FeatureFlagsProvider");
  }
  return context.flags?.useUnifiedRisks === true;
};

// Convenience hook: whether the in-app User Guide link should be shown (Features:ShowUserGuide
// backend flag). Defaults to visible until the flags have loaded, so we only hide when the
// backend has explicitly returned false (e.g. Production).
export const useShowUserGuide = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useShowUserGuide must be used within a FeatureFlagsProvider");
  }
  return context.flags?.showUserGuide !== false;
};

// Convenience hook: whether the Framework Risk Exposure chart on the AI assessment page should be
// shown (Features:ShowFrameworkRiskExposure backend flag). Defaults to visible until the flags have
// loaded, so we only hide when the backend has explicitly returned false (e.g. Production).
export const useShowFrameworkRiskExposure = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useShowFrameworkRiskExposure must be used within a FeatureFlagsProvider");
  }
  return context.flags?.showFrameworkRiskExposure !== false;
};

// Convenience hook: whether the main dashboard's org-wide Executive Report (PDF) button should be
// shown (Features:ShowExecutiveReport backend flag). Defaults to visible until the flags have
// loaded, so we only hide when the backend has explicitly returned false (e.g. Production).
export const useShowExecutiveReport = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useShowExecutiveReport must be used within a FeatureFlagsProvider");
  }
  return context.flags?.showExecutiveReport !== false;
};

// Convenience hook: whether the Download CSV Report button on the Enterprise assessment's
// compliance report modal should be shown (Features:ShowEnterpriseCsvReport backend flag). Defaults
// to visible until the flags have loaded, so we only hide when the backend has explicitly returned
// false (Staging, Production).
export const useShowEnterpriseCsvReport = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useShowEnterpriseCsvReport must be used within a FeatureFlagsProvider");
  }
  return context.flags?.showEnterpriseCsvReport !== false;
};

// Convenience hook: whether the AI Intelligence module is enabled (Features:ShowAiIntelligence
// backend flag) — gates the AI nav entry, the /AIIntelligence dashboard, and the AI Asset
// Discovery toggle on the Microsoft connection. Defaults to visible until the flags have loaded,
// so we only hide when the backend has explicitly returned false (e.g. Production).
export const useShowAiIntelligence = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useShowAiIntelligence must be used within a FeatureFlagsProvider");
  }
  return context.flags?.showAiIntelligence !== false;
};

// Convenience hook: whether the Security Insights GRC Framework Coverage card uses the
// server-computed calculation (Features:GrcCoverageV2 backend flag — official framework
// denominators + backend covered-ref lists). Opt-in: stays on the legacy client calculation
// until the backend explicitly returns true, including while flags are still loading.
export const useGrcCoverageV2 = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useGrcCoverageV2 must be used within a FeatureFlagsProvider");
  }
  return context.flags?.grcCoverageV2 === true;
};

// Convenience hook: whether the GRC Framework Coverage card is restricted to the org's vertical
// (Features:GrcVerticalFrameworks backend flag) — hides frameworks not applicable to the org's
// vertical (e.g. HIPAA/HITRUST for a non-Healthcare org). Independent of useGrcCoverageV2: this
// only changes which frameworks are listed, not any displayed percentage. Opt-in: stays on the
// legacy full-list behavior until the backend explicitly returns true.
export const useGrcVerticalFrameworks = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useGrcVerticalFrameworks must be used within a FeatureFlagsProvider");
  }
  return context.flags?.grcVerticalFrameworks === true;
};

// Convenience hook: whether the GRC readiness expansion is enabled (Features:GrcReadinessV2
// backend flag) — gates TPRM synthetic controls, the Evidence Collection tab, guided questions
// 11–15, and the normalized audit-readiness formula. Opt-in: stays on legacy behavior until the
// backend explicitly returns true, including while flags are still loading.
export const useGrcReadinessV2 = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useGrcReadinessV2 must be used within a FeatureFlagsProvider");
  }
  return context.flags?.grcReadinessV2 === true;
};

// Convenience hook: whether the Microsoft Defender XDR integration is enabled
// (Features:DefenderXdr backend flag) — gates the Defender XDR capability card, enable toggle,
// and sync/test panel on the Integrations page. Opt-in: stays hidden until the backend explicitly
// returns true, including while flags are still loading, so Production never flashes the panel.
export const useDefenderXdr = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useDefenderXdr must be used within a FeatureFlagsProvider");
  }
  return context.flags?.defenderXdr === true;
};

// Convenience hook: whether the Microsoft Purview DLP integration is enabled
// (Features:PurviewDlp backend flag) — gates the Purview card in Integration Settings and the
// DLP controls in Security Insights. Opt-in: stays hidden until the backend explicitly returns
// true, including while flags are still loading, because the integration needs a Graph consent
// (SecurityAlert.Read.All) most tenants have not granted.
export const usePurviewDlp = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("usePurviewDlp must be used within a FeatureFlagsProvider");
  }
  return context.flags?.purviewDlp === true;
};

// AI Intelligence preview surfaces (Features:AiIntelPreview). Off in every environment; hides the
// parts of the module that cannot show real data yet. Environment-wide, no per-org list.
export const useAiIntelPreview = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useAiIntelPreview must be used within a FeatureFlagsProvider");
  }
  return context.flags?.aiIntelPreview === true;
};

// Convenience hook: whether the Defender for Identity inventory is enabled
// (Features:DefenderIdentity backend flag) — gates the capability card and toggle on the
// Integrations page and the Identities tab on Security Insights. Opt-in like useDefenderXdr:
// hidden until the backend explicitly returns true.
export const useDefenderIdentity = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useDefenderIdentity must be used within a FeatureFlagsProvider");
  }
  return context.flags?.defenderIdentity === true;
};

// Convenience hook: whether the Security Operations (/soc) roll-up page is enabled
// (Features:Usoc backend flag) — gates the page, its route and its Enterprises dropdown entry.
// Opt-in like useDefenderXdr: hidden until the backend explicitly returns true, including while
// flags are still loading.
export const useUsoc = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useUsoc must be used within a FeatureFlagsProvider");
  }
  return context.flags?.usoc === true;
};

// Convenience hook: whether Universal Fusion is enabled (Features:Fusion backend flag) — gates the
// Dashboard Fusion panel, the clickable heatmap cells, the /Fusion page, its route and its nav tab.
// Opt-in: hidden until the backend explicitly returns true, including while flags are still loading.
export const useFusion = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFusion must be used within a FeatureFlagsProvider");
  }
  return context.flags?.fusion === true;
};

// Convenience hook: whether agent voice mode is enabled (Features:AgentVoice backend flag) —
// gates the mic and voice-mode buttons on the AI Agent page. Opt-in: stays hidden until the
// backend explicitly returns true, including while flags are still loading. The backend
// /Agent/transcribe and /Agent/speech endpoints 404 while the flag is off and 503 for regions
// without Azure OpenAI audio deployments.
export const useAgentVoice = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useAgentVoice must be used within a FeatureFlagsProvider");
  }
  return context.flags?.agentVoice === true;
};

// Convenience hook: voice-mode interaction style (Features:VoiceAutoDetect). false (default) =
// push-to-talk (tap to start, tap to send, mic off after each answer); true = hands-free
// auto-detect (listen on open, silence auto-submits, tap pauses/resumes).
export const useVoiceAutoDetect = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useVoiceAutoDetect must be used within a FeatureFlagsProvider");
  }
  return context.flags?.voiceAutoDetect === true;
};

// Convenience hook: whether the custom remediation workflows UI is enabled
// (Features:RemediationWorkflows backend flag) — gates the Settings → Workflows nav entries and
// /settings/workflows route, the Workflow button on Remediations cards, and the workflow panel in
// RiskManagementModal. UI-only: the backend workflow endpoints are not gated. Opt-in: stays hidden
// until the backend explicitly returns true, including while flags are still loading.
export const useRemediationWorkflows = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useRemediationWorkflows must be used within a FeatureFlagsProvider");
  }
  return context.flags?.remediationWorkflows === true;
};

// Convenience hook: whether the Executive Remediation Scorecard is switched on for this org
// (per-org setting returned as remediationScorecard) — gates the Scorecard nav entry and the
// /remediation-scorecard route. Opt-in: stays hidden until the backend explicitly returns true,
// including while flags are still loading.
export const useRemediationScorecard = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useRemediationScorecard must be used within a FeatureFlagsProvider");
  }
  return context.flags?.remediationScorecard === true;
};

// True once the flags have been fetched for the current org (or the fetch failed). Route guards on
// opt-in flags render nothing until then, so a direct URL hit is not redirected before the flags
// arrive.
export const useFeatureFlagsLoaded = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlagsLoaded must be used within a FeatureFlagsProvider");
  }
  return context.loaded === true;
};

// Convenience hook: the deployment profile the backend was started with (Deployment:Profile —
// "saas" | "connected" | "restricted" | "airgapped"), the WorkerCatalog keys of the hosted
// services it did not register for that profile, and whether the configured secrets provider can
// persist secrets (false for Secrets:Provider=Environment, so connector credentials cannot be
// saved from the UI). Also the backend egress catalog's verdicts: `modules` ({ "<moduleId>": bool })
// and `connectorSources` ({ "<SOURCE_KEY>": bool }), null until flags load or on an older backend.
// Read-only hints; defaults describe the SaaS deployment until flags load. Components read the two
// maps through hooks/useModuleAvailability.js, which treats a missing map as "everything available".
export const useDeploymentProfile = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useDeploymentProfile must be used within a FeatureFlagsProvider");
  }
  const flags = context.flags || {};
  return {
    profile: flags.deploymentProfile ?? "saas",
    disabledWorkers: flags.disabledWorkers ?? [],
    secretsWritable: flags.secretsWritable ?? true,
    modules: flags.modules ?? null,
    connectorSources: flags.connectorSources ?? null,
  };
};

// Offline licence status for the licence banner: the `licence` block of GET /config/features,
// { required, state, expiresAt, daysRemaining } with state one of notRequired | valid |
// expiringSoon | grace | readOnly | none. DEFAULT_LICENCE_STATUS until the flags load or when the
// backend omits the field.
export const useLicenceStatus = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useLicenceStatus must be used within a FeatureFlagsProvider");
  }
  return context.licence ?? DEFAULT_LICENCE_STATUS;
};

// Re-fetches GET /config/features, e.g. after a licence upload so the banner and module
// availability change without a page reload.
export const useRefetchFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useRefetchFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context.refetchFlags;
};
